import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { useIsFormPristine } from '@gef-ui/form-core/hooks/state/useFormState';
import { resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';
import Checkbox from '@gef-ui/form-material/Checkbox';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { MaxLengths } from 'constants/forms';
import { previous as setPreviousContextUser } from 'features/views/userContext/actions';
import { getContextUser } from 'features/views/userContext/selectors';
import {
	fetchSubordinateUsers,
	handleReload,
	loadFormFromTemporaryStore,
	saveFormToTemporaryStore,
} from 'features/views/usersSubordinate/actions';
import { FIELDS, SUBORDINATE_USERS_FORM_NAME } from 'features/views/usersSubordinate/form';

export const SubordinateUsersForm = ({ resettingSubordinateUsersOnly, setResettingSubordinateUsersOnly }) => {
	const dispatch = useDispatch();

	const next = useControlNext(SUBORDINATE_USERS_FORM_NAME);
	const formData = useFormData();
	const formIsInitiated = isObject(formData);
	const isFormPristine = useIsFormPristine();
	const contextUser = useSelector(getContextUser);

	useEffect(() => {
		// only for initial mount - changes of contextUser are not considered
		if (formIsInitiated) {
			if (contextUser) {
				// for cases when user is coming back from other pages to subordinateUsers page
				// reseting back previous contextUser and reloading to get last fetched results (involving correct page)
				dispatch(setPreviousContextUser());
				dispatch(loadFormFromTemporaryStore());
				dispatch(handleReload());
			} else {
				// basic initial mount with for no contextUser case
				dispatch(fetchSubordinateUsers());
			}
		}
		// eslint-disable-next-line
	}, [dispatch, formIsInitiated]);

	useEffect(() => {
		// logic for comming back to subordinateUsers page while being there under other contextUser
		if (resettingSubordinateUsersOnly) {
			dispatch(loadFormFromTemporaryStore());
			dispatch(handleReload());
			setResettingSubordinateUsersOnly(false);
		} else {
			dispatch(handleReload());
		}
	}, [dispatch, contextUser, resettingSubordinateUsersOnly, setResettingSubordinateUsersOnly]);

	const handleSubmit = useCallback(() => {
		dispatch(fetchSubordinateUsers());
		dispatch(saveFormToTemporaryStore());
	}, [dispatch]);

	const handleReset = useCallback(async () => {
		next(resetForm());
		dispatch(saveFormToTemporaryStore());
		dispatch(fetchSubordinateUsers());
	}, [dispatch, next]);

	const handleActivateCheckbox = useCallback(() => {
		if (isFormPristine) {
			next(setValue({ fieldName: FIELDS.searchForAllSubordinate, value: true }));
		}
	}, [next, isFormPristine]);

	return (
		<Grid container spacing={2}>
			<Grid size={4}>
				<TextField
					size="small"
					fieldName={FIELDS.surname}
					label={getLabelMessage('subordinateUsers:surname')}
					onChange={handleActivateCheckbox}
					slotProps={{
						htmlInput: {
							maxLength: MaxLengths.SURNAME,
						},
					}}
				/>
			</Grid>
			<Grid size={4}>
				<TextField
					size="small"
					fieldName={FIELDS.firstName}
					label={getLabelMessage('subordinateUsers:firstName')}
					onChange={handleActivateCheckbox}
					slotProps={{
						htmlInput: {
							maxLength: MaxLengths.NAME,
						},
					}}
				/>
			</Grid>
			<Tooltip title={getLabelMessage('tooltips:sellerIn')}>
				<Grid size={4}>
					<TextField
						size="small"
						fieldName={FIELDS.sellerIn}
						label={getLabelMessage('subordinateUsers:sellerIn')}
						onChange={handleActivateCheckbox}
						slotProps={{
							htmlInput: {
								maxLength: MaxLengths.SELLER_IN,
							},
						}}
					/>
				</Grid>
			</Tooltip>
			<Grid size={4}>
				<Checkbox
					fieldName={FIELDS.searchForAllSubordinate}
					label={getLabelMessage('subordinateUsers:searchForAllSubordinate')}
				/>
			</Grid>
			<Grid size={4} />
			<Grid size={2}>
				<Button variant="outlined" onClick={handleReset} fullWidth>
					{getLabelMessage('common:resetButton')}
				</Button>
			</Grid>
			<Grid size={2}>
				<SubmitButton
					variant="contained"
					onClick={handleSubmit}
					fullWidth
					text={getLabelMessage('common:searchButton')}
				/>
			</Grid>
		</Grid>
	);
};

SubordinateUsersForm.propTypes = {
	resettingSubordinateUsersOnly: PropTypes.bool,
	setResettingSubordinateUsersOnly: PropTypes.func,
};

export default SubordinateUsersForm;
